import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getSubscriptions, upgradeSubscription } from 'services/subscriptions/subscriptionService';
import { createStripeCheckoutSession } from 'services/subscriptions/paymentService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';
import { useSidebar } from 'contexts/SidebarContext';
import { Gem, Rocket, CheckCircle2, AlertTriangle, RotateCcw, Menu, Sparkles } from 'lucide-react';
import { getErrorMessage } from 'utils/errors';

// Billing cycle type
type BillingCycle = 'monthly' | 'annual';

// Define interfaces for state and props
interface Price {
  amount_monthly: number;
  amount_annually: number;
  amount_4weeks?: number;
  amount_4weeks_annual?: number;
  amount_yearly?: number;
  discount_percentage?: number;
  currency?: string;
  country?: string;
}

interface ActionDetail {
  _id: string;
  name: string;
  friendly_name: string;
  category: string;
  platform: string;
  description: string;
  allowed: boolean;
  settings_type: string;
  is_active: boolean;
  status: string;
  created_on: string;
  updated_on: string;
}

interface Action {
  action_id: string;
  action_limit: number;
  limit_unit?: 'day' | 'week';
  action_details?: ActionDetail;
  limit_utilized?: number;
}

interface PlanFeatures {
  likes?: {
    min: number;
    max: number;
    unit: string;
  };
  follow_unfollow?: {
    enabled: boolean;
    description: string;
  };
  reposting?: {
    limit: number;
    unit: string;
    description: string;
  };
  ai_comments?: {
    min: number;
    max: number;
    unit: string;
  };
  ai_auto_replies?: {
    enabled: boolean;
    features: string[];
  };
}

interface StripePriceIds {
  monthly?: string;
  annual?: string;
}

type LocalizedText = string | { en: string; fr: string };

interface SubscriptionPlan {
  _id: string;
  subscription_name: string;
  description: LocalizedText;
  tagline?: LocalizedText;
  target_audience?: string;
  highlighted_text?: string;
  tenure: number;
  price: Price;
  number_of_accounts: number;
  actions: Action[];
  status: string;
  is_active: boolean;
  social_accounts: string[];
  created_at: string;
  supported_platforms: string[];
  color_indicator?: string;
  compliance_note?: string;
  features?: PlanFeatures;
  setup_type?: string;
  support_level?: string;
  tier?: number;
  volume_type?: string;
  stripe_price_ids?: StripePriceIds;
}

interface RootState {
  user: {
    isExpired: boolean;
    planName: string;
    hasTrialed: boolean;
  };
}

interface ExcessAccountsError {
  message: string;
  code: string;
  activeAccountsCount: number;
  allowedAccounts: number;
  excessAccounts: number;
}

const Subscriptions: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('annual');
  const [excessAccountsError, setExcessAccountsError] = useState<ExcessAccountsError | null>(null);
  const isExpired = useSelector((state: RootState) => state.user.isExpired);
  const planName = useSelector((state: RootState) => state.user.planName);
  const hasTrialed = useSelector((state: RootState) => state.user.hasTrialed);

  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();

  // Color tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#94A3B8' : '#64748B';
  const cardBg = isDark ? '#1D293D' : '#FFFFFF';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const textPrimary = isDark ? '#E2E8F0' : '#1E293B';
  const textSecondary = isDark ? '#94A3B8' : '#64748B';
  const textMuted = isDark ? '#64748B' : '#94A3B8';
  const iconContainerBg = isDark ? '#1C398E' : '#EFF6FF';
  const iconColor = isDark ? '#51A2FF' : '#2B7FFF';
  const toggleBg = isDark ? '#1D293D' : '#F1F5F9';
  const toggleActiveBg = isDark ? '#0F172B' : '#FFFFFF';
  const toggleActiveShadow = isDark ? '0px 1px 2px rgba(0,0,0,0.3)' : '0px 1px 2px rgba(0,0,0,0.1)';
  const greenBadgeBg = isDark ? 'rgba(34, 197, 94, 0.15)' : '#DCFCE7';
  const greenBadgeText = isDark ? '#4ADE80' : '#16A34A';
  const sectionBorder = isDark ? '#314158' : '#F1F5F9';

  const handleUpgrade = async (plan: SubscriptionPlan, isDowngrade = false) => {
    if (plan.subscription_name === 'Trial' && isExpired) return;

    const action = isDowngrade ? 'downgrade' : 'upgrade';

    try {
      // Check if this is a paid plan (not Trial)
      const isPaidPlan = plan.subscription_name !== 'Trial' && plan.price.amount_monthly > 0;

      if (isPaidPlan) {
        // Get the Stripe price ID based on billing cycle from the plan data
        const stripePriceId = billingCycle === 'annual'
          ? plan.stripe_price_ids?.annual
          : plan.stripe_price_ids?.monthly;

        if (!stripePriceId) {
          toast.error(t('subscriptions.errors.stripePriceIdNotConfigured') || 'Stripe price ID not configured for this plan');
          return;
        }

        // Show loading state
        toast.loading(t('subscriptions.creatingCheckoutSession') || 'Creating checkout session...');

        // Create Stripe checkout session
        const checkoutResponse = await createStripeCheckoutSession(
          plan._id,
          stripePriceId,
          billingCycle
        );

        // Remove loading toast
        toast.dismiss();

        if (checkoutResponse.success && checkoutResponse.url) {
          // Redirect to Stripe Checkout
          window.location.href = checkoutResponse.url;
        } else if (checkoutResponse.success && checkoutResponse.mode === 'plan_switched') {
          // Mid-trial plan switch — backend already updated Stripe, no checkout needed.
          // Trial end date is preserved; user just changed which plan they're trialing.
          toast.success(t('subscriptions.planSwitched', 'Plan switched. Your trial end date is unchanged.'));
          setTimeout(() => window.location.reload(), 1500);
        } else {
          toast.error(checkoutResponse.message || t(`subscriptions.errors.failedTo${isDowngrade ? 'Downgrade' : 'CreateSession'}`, `Failed to ${action} subscription`));
        }
      } else {
        // For Trial or free plans, use the old upgrade method
        const response = await upgradeSubscription();
        if (response.status === 200) {
          window.location.reload();
        } else {
          toast.error(response.data?.message || t('subscriptions.errors.failedToUpgrade', `Failed to ${action} subscription`));
        }
      }
    } catch (error: unknown) {
      toast.dismiss(); // Remove any loading toasts
      const errResp = (error as { response?: { data?: ExcessAccountsError & { code?: string } } })?.response?.data;
      if (errResp?.code === 'EXCESS_ACCOUNTS') {
        setExcessAccountsError(errResp);
      } else {
        const errorMessage = getErrorMessage(error, t('subscriptions.errors.failedToUpgrade', `Failed to ${action} subscription`));
        toast.error(errorMessage);
      }
    }
  };

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getSubscriptions();

        if (!response.plans || !Array.isArray(response.plans)) {
          throw new Error(t('subscriptions.errors.invalidPlansData'));
        }

        const validatedPlans: SubscriptionPlan[] = response.plans.map((plan: SubscriptionPlan) => ({
          _id: plan._id || '',
          subscription_name: plan.subscription_name || t('subscriptions.unknownPlan'),
          description: plan.description || { en: t('subscriptions.noDescription'), fr: t('subscriptions.noDescription') },
          tagline: plan.tagline || { en: '', fr: '' },
          target_audience: plan.target_audience || '',
          highlighted_text: plan.highlighted_text || '',
          tenure: plan.tenure || 0,
          number_of_accounts: plan.number_of_accounts || 1,
          price: plan.price || {
            amount_monthly: 0,
            amount_annually: 0,
            currency: 'EUR',
            country: 'FR',
          },
          actions: Array.isArray(plan.actions)
            ? plan.actions.map((action: Action) => ({
                ...action,
                action_details: action.action_details
                  ? {
                      _id: action.action_details._id || '',
                      name: action.action_details.name || '',
                      friendly_name: action.action_details.friendly_name || t('subscriptions.unknownAction'),
                      category: action.action_details.category || '',
                      platform: action.action_details.platform || '',
                      description: action.action_details.description || t('subscriptions.noDescription'),
                      allowed: action.action_details.allowed ?? true,
                      settings_type: action.action_details.settings_type || '',
                      is_active: action.action_details.is_active ?? true,
                      status: action.action_details.status || '',
                      created_on: action.action_details.created_on || '',
                      updated_on: action.action_details.updated_on || '',
                    }
                  : undefined,
                limit_utilized: action.limit_utilized || 0,
              }))
            : [],
          status: plan.status || 'active',
          is_active: plan.is_active ?? true,
          social_accounts: Array.isArray(plan.social_accounts) ? plan.social_accounts : [],
          created_at: plan.created_at || '',
          supported_platforms: Array.isArray(plan.supported_platforms) ? plan.supported_platforms : [],
          color_indicator: plan.color_indicator || '',
          compliance_note: plan.compliance_note || '',
          features: plan.features || undefined,
          setup_type: plan.setup_type || '',
          support_level: plan.support_level || 'standard',
          tier: plan.tier || 0,
          volume_type: plan.volume_type || '',
          stripe_price_ids: plan.stripe_price_ids || undefined,
        }));

        // Sort plans: active plan first, then by tier ascending
        const sortedPlans = validatedPlans.sort((a, b) => {
          const aIsActive = a.subscription_name === planName && !isExpired;
          const bIsActive = b.subscription_name === planName && !isExpired;
          if (aIsActive && !bIsActive) return -1;
          if (!aIsActive && bIsActive) return 1;
          return (a.tier || 0) - (b.tier || 0);
        });

        setPlans(sortedPlans);
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401) {
          toast.error(t('subscriptions.errors.sessionExpired'));
          navigate('/');
          return;
        }
        const errorMessage = getErrorMessage(error, t('subscriptions.errors.failedToLoad'));
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, [navigate, t]);

  if (loading) return <Spinner size="large" text={t('common.loading')} fullPage />;
  if (error) return (
    <div className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0" style={{ gap: '8px', background: containerBg }}>
      <div className="flex flex-col flex-1 min-h-0 w-full overflow-hidden items-center justify-center rounded-none lg:rounded-2xl" style={{ background: mainBg, boxShadow: mainShadow }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 24px' }}>
          <div style={{ marginBottom: '12px' }}>
            <AlertTriangle size={32} color={isDark ? '#EF4444' : '#DC2626'} />
          </div>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: titleColor, marginBottom: '6px' }}>{t('common.error')}</h3>
          <p style={{ fontSize: '12px', color: subtitleColor }}>{error}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0" style={{ gap: '8px', background: containerBg }}>
      <div className="flex flex-col items-start flex-1 min-h-0 w-full overflow-hidden rounded-none lg:rounded-2xl" style={{ background: mainBg, boxShadow: mainShadow }}>
        {/* HEADER */}
        <div className="flex-shrink-0 w-full" style={{ borderBottom: `1px solid ${headerBorder}`, display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px 16px' }}>
          {/* Top row: hamburger + icon + title + billing toggle (desktop) */}
          <div className="flex items-center" style={{ gap: '8px' }}>
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            <div className="hidden lg:flex" style={{ width: '36px', height: '36px', borderRadius: '12px', background: iconContainerBg, alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Gem size={20} color={iconColor} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ fontSize: '20px', fontWeight: 700, color: titleColor, margin: 0, lineHeight: '24px' }}>{t('navigation.subscriptions') || 'Subscriptions'}</h1>
              <p className="hidden sm:block" style={{ fontSize: '12px', color: subtitleColor, margin: 0 }}>{t('subscriptions.selectPlan')}</p>
            </div>
            {/* Billing toggle - desktop only */}
            <div className="hidden sm:flex" style={{ alignItems: 'center', gap: '2px', background: toggleBg, padding: '4px', borderRadius: '12px', flexShrink: 0 }}>
              <button
                onClick={() => setBillingCycle('monthly')}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: billingCycle === 'monthly' ? toggleActiveBg : 'transparent',
                  color: billingCycle === 'monthly' ? titleColor : textSecondary,
                  boxShadow: billingCycle === 'monthly' ? toggleActiveShadow : 'none',
                }}
              >
                {t('subscriptions.monthly') || 'Monthly'}
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: billingCycle === 'annual' ? toggleActiveBg : 'transparent',
                  color: billingCycle === 'annual' ? titleColor : textSecondary,
                  boxShadow: billingCycle === 'annual' ? toggleActiveShadow : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {t('subscriptions.annual') || 'Annual'}
                <span style={{ background: greenBadgeBg, color: greenBadgeText, fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>-20%</span>
              </button>
            </div>
          </div>
          {/* Billing toggle - mobile only */}
          <div className="flex sm:hidden" style={{ alignItems: 'center', gap: '2px', background: toggleBg, padding: '4px', borderRadius: '12px' }}>
            <button
              onClick={() => setBillingCycle('monthly')}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: '13px',
                fontWeight: 600,
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: billingCycle === 'monthly' ? toggleActiveBg : 'transparent',
                color: billingCycle === 'monthly' ? titleColor : textSecondary,
                boxShadow: billingCycle === 'monthly' ? toggleActiveShadow : 'none',
              }}
            >
              {t('subscriptions.monthly') || 'Monthly'}
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: '13px',
                fontWeight: 600,
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: billingCycle === 'annual' ? toggleActiveBg : 'transparent',
                color: billingCycle === 'annual' ? titleColor : textSecondary,
                boxShadow: billingCycle === 'annual' ? toggleActiveShadow : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              {t('subscriptions.annual') || 'Annual'}
              <span style={{ background: greenBadgeBg, color: greenBadgeText, fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>-20%</span>
            </button>
          </div>
        </div>

        {/* SCROLLABLE CONTENT */}
        <div className={`overflow-y-auto flex-1 ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ padding: '16px', width: '100%' }}>
          {/* First-time banner — only for users without an active subscription who haven't
              already burned their free trial. The backend's trial-eligibility check would
              deny a returning customer anyway, so promising a 7-day trial here would be a lie. */}
          {isExpired && !hasTrialed && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '14px 18px',
                marginBottom: '16px',
                borderRadius: '12px',
                background: isDark ? 'linear-gradient(90deg, #1C398E 0%, #4C1D95 100%)' : 'linear-gradient(90deg, #EFF6FF 0%, #F5F3FF 100%)',
                border: `1px solid ${isDark ? '#1E3A8A' : '#BFDBFE'}`,
              }}
            >
              <Sparkles size={20} color={isDark ? '#FBBF24' : '#D97706'} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: isDark ? '#E2E8F0' : '#1E3A8A', marginBottom: '2px' }}>
                  {t('subscriptions.selectPlanToStart', 'Select a plan to start your 7-day free trial')}
                </div>
                <div style={{ fontSize: '12px', color: isDark ? '#BFDBFE' : '#3730A3', lineHeight: '18px' }}>
                  {t('subscriptions.cardRequiredNote', 'Card required — no charge until your trial ends. Cancel anytime.')}
                </div>
              </div>
            </div>
          )}
          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3" style={{ gap: '16px', alignItems: 'stretch' }}>
            {(() => {
              const currentPlanTier = plans.find(p => p.subscription_name === planName)?.tier || 0;
              return plans.map((plan, index) => {
              const isTrial = plan.subscription_name === 'Trial';
              const isActivePlan = plan.subscription_name === planName && !isExpired;
              const planTier = plan.tier || 0;
              const isDowngrade = !isTrial && !isActivePlan && planTier < currentPlanTier && !isExpired;

              // Get color indicator value based on plan
              const getColorIndicatorValue = () => {
                if (isTrial) return '#A855F7';
                switch (plan.color_indicator) {
                  case 'green': return '#22C55E';
                  case 'blue': return '#3B82F6';
                  case 'red': return '#EF4444';
                  default: return '#9CA3AF';
                }
              };

              // Get support level text
              const getSupportLevel = () => {
                switch (plan.support_level) {
                  case 'standard': return t('subscriptions.planFeatures.standardSupport');
                  case 'priority': return t('subscriptions.planFeatures.prioritySupport');
                  case 'human_priority': return t('subscriptions.planFeatures.humanPrioritySupport');
                  default: return t('subscriptions.planFeatures.standardSupport');
                }
              };

              // Get setup type text
              const getSetupType = () => {
                switch (plan.setup_type) {
                  case 'simple': return t('subscriptions.planFeatures.simpleSetup');
                  case 'advanced': return t('subscriptions.planFeatures.advancedSetup');
                  case 'highly_advanced': return t('subscriptions.planFeatures.highlyAdvancedSetup');
                  default: return t('subscriptions.planFeatures.simpleSetup');
                }
              };

              // Check if Trial should be disabled (user already used trial or is on paid plan)
              const isTrialDisabled = isTrial && (planName !== 'Trial' || (planName === 'Trial' && isExpired));

              // Helper functions for clean text rendering
              const getLocalizedText = (value: string | { en: string; fr: string } | undefined): string => {
                if (!value) return '';
                if (typeof value === 'string') return value;
                return value[lang] || value.en || '';
              };

              const renderDescription = () => {
                return getLocalizedText(plan.description);
              };

              const renderFollowUnfollow = () => {
                const texts: Record<string, string> = {
                  'Trial': t('subscriptions.planFeatures.basicFollowUnfollow'),
                  'Starter': t('subscriptions.planFeatures.smartFollowUnfollow'),
                  'Pro': t('subscriptions.planFeatures.smartFollowUnfollowExtended'),
                  'Elite': t('subscriptions.planFeatures.smartFollowUnfollowIncreased'),
                };
                return texts[plan.subscription_name] || plan.features?.follow_unfollow?.description || '';
              };

              const renderComplianceNote = () => {
                const notes: Record<string, string> = {
                  'Trial': t('subscriptions.planFeatures.safeCompliant'),
                  'Starter': t('subscriptions.planFeatures.moderateCompliant'),
                  'Pro': t('subscriptions.planFeatures.highVolumeSecured'),
                  'Elite': t('subscriptions.planFeatures.veryHighVolumeOptimized'),
                };
                return notes[plan.subscription_name] || plan.compliance_note || '';
              };

              const renderSetupType = () => {
                const types: Record<string, string> = {
                  'Trial': t('subscriptions.planFeatures.quickEasySetup'),
                  'Starter': t('subscriptions.planFeatures.simpleSetup'),
                  'Pro': t('subscriptions.planFeatures.advancedSetup'),
                  'Elite': t('subscriptions.planFeatures.highlyAdvancedSetup'),
                };
                return types[plan.subscription_name] || getSetupType();
              };

              const renderRepostingDescription = () => {
                const descriptions: Record<string, string> = {
                  'Starter': t('subscriptions.planFeatures.reposting1PerWeek'),
                  'Pro': t('subscriptions.planFeatures.repostingUpTo5'),
                  'Elite': t('subscriptions.planFeatures.repostingAllTop'),
                };
                return descriptions[plan.subscription_name] || plan.features?.reposting?.description || '';
              };

              const renderHighlightedText = () => {
                const highlights: Record<string, string> = {
                  'Trial': t('subscriptions.cardLabels.highlightTrial'),
                  'Starter': t('subscriptions.cardLabels.highlightStarter'),
                  'Pro': t('subscriptions.cardLabels.highlightPro'),
                  'Elite': t('subscriptions.cardLabels.highlightElite'),
                };
                return highlights[plan.subscription_name] || plan.highlighted_text || '';
              };

              return (
                <div
                  key={index}
                  style={{
                    background: isActivePlan ? (isDark ? '#0F2818' : '#F0FDF4') : cardBg,
                    borderRadius: '16px',
                    border: isActivePlan ? '2px solid #22C55E' : `1px solid ${cardBorder}`,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    opacity: isTrialDisabled ? 0.6 : 1,
                    boxShadow: isActivePlan ? (isDark ? '0 0 16px rgba(34, 197, 94, 0.25)' : '0 0 16px rgba(34, 197, 94, 0.2)') : 'none',
                  }}
                >
                  {/* Card Header with colored top border */}
                  <div style={{ height: '4px', background: isActivePlan ? '#22C55E' : getColorIndicatorValue() }} />

                  {/* Card Content */}
                  <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Header - Plan name */}
                    <div style={{ marginBottom: '12px' }}>
                      <h2 style={{ fontSize: '12px', fontWeight: 700, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                        {plan.subscription_name}
                      </h2>
                      {isTrial ? (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                          <span style={{ fontSize: '20px', fontWeight: 700, color: '#22C55E' }}>{t('subscriptions.free')}</span>
                          <span style={{ fontSize: '12px', color: textMuted }}>/ {plan.tenure} {t('subscriptions.day')}s</span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                          <span style={{ fontSize: '20px', fontWeight: 700, color: textPrimary }}>
                            &euro;{billingCycle === 'annual'
                              ? plan.price.amount_4weeks_annual || plan.price.amount_monthly
                              : plan.price.amount_4weeks || plan.price.amount_monthly}
                          </span>
                          <span style={{ fontSize: '12px', color: textMuted }}>/ {t('subscriptions.fourWeeks')}</span>
                          {billingCycle === 'annual' && (
                            <span style={{ background: greenBadgeBg, color: greenBadgeText, fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 700, marginLeft: '4px' }}>
                              -{plan.price.discount_percentage || 20}%
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Tagline */}
                    {(plan.tagline || isTrial) && (
                      <p style={{ fontSize: '12px', fontWeight: 600, color: textPrimary, marginBottom: '12px' }}>
                        {isTrial ? t('subscriptions.cardLabels.tryFree') : getLocalizedText(plan.tagline)}
                      </p>
                    )}

                    {/* Description */}
                    <p style={{ fontSize: '12px', color: textSecondary, lineHeight: '1.6', marginBottom: '16px' }}>
                      {renderDescription()}
                    </p>

                    {/* Pricing Section */}
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ fontSize: '12px', fontWeight: 600, color: textPrimary, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#F59E0B' }}>&#128176;</span>
                        {t('subscriptions.pricing')}
                      </h4>
                      {isTrial ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '4px' }}>
                          <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; <span style={{ fontWeight: 600, color: '#22C55E' }}>{t('subscriptions.cardLabels.free')}</span> {t('subscriptions.forDays', { days: plan.tenure })}</p>
                          <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; {t('subscriptions.cardLabels.noCreditCard')}</p>
                          <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; {t('subscriptions.cardLabels.noCommitmentLabel')}</p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '4px' }}>
                          <p style={{ fontSize: '12px', color: billingCycle === 'monthly' ? textPrimary : textSecondary, fontWeight: billingCycle === 'monthly' ? 500 : 400, margin: 0 }}>
                            &#8226; <span style={{ fontWeight: 600, color: textPrimary }}>&euro;{plan.price.amount_4weeks || plan.price.amount_monthly}</span> / {t('subscriptions.fourWeeks')} <span style={{ color: textMuted }}>({t('subscriptions.noCommitment')})</span>
                            {billingCycle === 'monthly' && <span style={{ color: iconColor, marginLeft: '4px' }}>&larr; {t('subscriptions.cardLabels.selected')}</span>}
                          </p>
                          <p style={{ fontSize: '12px', color: billingCycle === 'annual' ? textPrimary : textSecondary, fontWeight: billingCycle === 'annual' ? 500 : 400, margin: 0 }}>
                            &#8226; <span style={{ fontWeight: 600, color: textPrimary }}>&euro;{plan.price.amount_4weeks_annual || plan.price.amount_monthly}</span> / {t('subscriptions.fourWeeks')} <span style={{ color: textMuted }}>({t('subscriptions.cardLabels.annual')} &ndash; {plan.price.discount_percentage || 20}% {t('subscriptions.off')})</span>
                            {billingCycle === 'annual' && <span style={{ color: iconColor, marginLeft: '4px' }}>&larr; {t('subscriptions.cardLabels.selected')}</span>}
                          </p>
                          {billingCycle === 'annual' && (
                            <p style={{ paddingLeft: '12px', color: textMuted, fontSize: '12px', margin: 0 }}>&rarr; &euro;{plan.price.amount_yearly || plan.price.amount_annually} / {t('subscriptions.year')}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* What's Included Section */}
                    <div style={{ marginBottom: '16px', flex: 1 }}>
                      <h4 style={{ fontSize: '12px', fontWeight: 600, color: textPrimary, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#22C55E' }}>&#10003;</span>
                        {t('subscriptions.whatsIncluded')}
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingLeft: '4px' }}>
                        {/* Number of accounts */}
                        <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; {plan.subscription_name === 'Elite' ? (
                          t('subscriptions.planFeatures.multiAccountManagement')
                        ) : (
                          <><span style={{ fontWeight: 600, color: textPrimary }}>{plan.number_of_accounts}</span> {t('subscriptions.instagramAccount')}</>
                        )}</p>

                        {/* Automated likes */}
                        {plan.features?.likes && (
                          <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; {t('subscriptions.planFeatures.automatedLikes')}: <span style={{ fontWeight: 600, color: textPrimary }}>{plan.features.likes.min} {t('subscriptions.to')} {plan.features.likes.max}</span> {t('subscriptions.per')} {t('subscriptions.day')}</p>
                        )}

                        {/* Follow/Unfollow */}
                        {plan.features?.follow_unfollow?.enabled && (
                          <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; {renderFollowUnfollow()}</p>
                        )}

                        {/* Setup type */}
                        <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; {renderSetupType()}</p>

                        {/* Compliance note */}
                        {plan.compliance_note && (
                          <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; {renderComplianceNote()}</p>
                        )}

                        {/* Support level */}
                        <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; {getSupportLevel()}</p>

                        {/* Automatic reposting for Trial */}
                        {isTrial && (
                          <>
                            <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; {t('subscriptions.planFeatures.automaticReposting')}</p>
                            <p style={{ paddingLeft: '12px', color: textMuted, fontSize: '12px', margin: 0 }}>&#9702; {t('subscriptions.planFeatures.notAvailableInTrial')}</p>
                          </>
                        )}

                        {/* Automatic reposting */}
                        {!isTrial && plan.features?.reposting && (
                          <>
                            <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; <span style={{ fontWeight: 600, color: textPrimary }}>{t('subscriptions.planFeatures.automaticReposting')}</span></p>
                            <p style={{ paddingLeft: '12px', color: textMuted, fontSize: '12px', margin: 0 }}>&#9702; {renderRepostingDescription()}</p>
                          </>
                        )}

                        {/* AI-powered comments for Trial */}
                        {isTrial && (
                          <>
                            <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; {t('subscriptions.planFeatures.aiPoweredComments')}</p>
                            <p style={{ paddingLeft: '12px', color: textMuted, fontSize: '12px', margin: 0 }}>&#9702; 0 {t('subscriptions.to')} 5 {t('subscriptions.messagesPerDay')}</p>
                          </>
                        )}

                        {/* AI-powered comments */}
                        {!isTrial && plan.features?.ai_comments && (
                          <>
                            <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; <span style={{ fontWeight: 600, color: textPrimary }}>{t('subscriptions.planFeatures.aiPoweredComments')}</span></p>
                            <p style={{ paddingLeft: '12px', color: textMuted, fontSize: '12px', margin: 0 }}>&#9702; {plan.features.ai_comments.min} {t('subscriptions.to')} {plan.features.ai_comments.max} {t('subscriptions.messagesPerDay')}</p>
                          </>
                        )}

                        {/* AI auto-replies */}
                        {plan.features?.ai_auto_replies?.enabled && (
                          <>
                            <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>&#8226; <span style={{ fontWeight: 600, color: textPrimary }}>{plan.subscription_name === 'Elite' ? t('subscriptions.planFeatures.ultraOptimizedAiAutoReplies') : t('subscriptions.planFeatures.optimizedAiAutoReplies')}</span></p>
                            {plan.features.ai_auto_replies.features?.map((feature, idx) => (
                              <p key={idx} style={{ paddingLeft: '12px', color: textMuted, fontSize: '12px', margin: 0 }}>&#9702; {feature}</p>
                            ))}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Highlighted Text / Recommendation */}
                    {(plan.highlighted_text || isTrial) && (
                      <div style={{ marginTop: 'auto', paddingTop: '12px', borderTop: `1px solid ${sectionBorder}` }}>
                        <p style={{ fontSize: '12px', color: textSecondary, lineHeight: '1.6', display: 'flex', alignItems: 'flex-start', gap: '6px', margin: 0 }}>
                          <span style={{ color: '#F59E0B', flexShrink: 0 }}>&#128073;</span>
                          <span>{renderHighlightedText()}</span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Card Footer - Action Button */}
                  <div style={{ padding: '0 20px 20px' }}>
                    {isTrial ? (
                      planName === 'Trial' && !isExpired ? (
                        <button
                          style={{
                            width: '100%',
                            padding: '10px',
                            background: '#22C55E',
                            color: '#FFFFFF',
                            borderRadius: '8px',
                            border: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                          disabled
                        >
                          <CheckCircle2 size={14} />
                          {t('subscriptions.active')}
                        </button>
                      ) : (
                        <button
                          style={{
                            width: '100%',
                            padding: '10px',
                            background: isDark ? '#1D293D' : '#F1F5F9',
                            color: textMuted,
                            borderRadius: '8px',
                            border: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'not-allowed',
                          }}
                          disabled
                        >
                          {t('subscriptions.trialUsed')}
                        </button>
                      )
                    ) : (
                      plan.subscription_name === planName && isExpired ? (
                        <button
                          style={{
                            width: '100%',
                            padding: '10px',
                            background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                            color: '#FFFFFF',
                            borderRadius: '8px',
                            border: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                          onClick={() => handleUpgrade(plan)}
                        >
                          <RotateCcw size={14} />
                          {t('subscriptions.renew').toUpperCase()}
                        </button>
                      ) : plan.subscription_name === planName && !isExpired ? (
                        <button
                          style={{
                            width: '100%',
                            padding: '10px',
                            background: '#22C55E',
                            color: '#FFFFFF',
                            borderRadius: '8px',
                            border: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                          disabled
                        >
                          <CheckCircle2 size={14} />
                          {t('subscriptions.active')}
                        </button>
                      ) : (
                        <button
                          style={{
                            width: '100%',
                            padding: '10px',
                            background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                            color: '#FFFFFF',
                            borderRadius: '8px',
                            border: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                          onClick={() => handleUpgrade(plan, isDowngrade)}
                        >
                          <Rocket size={14} />
                          {isDowngrade
                            ? t('subscriptions.downgrade', 'DOWNGRADE').toUpperCase()
                            : t('subscriptions.upgrade').toUpperCase()}
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            });
            })()}
          </div>
        </div>
      </div>

      {/* Excess Accounts Error Modal */}
      {excessAccountsError && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setExcessAccountsError(null); }}
        >
          <div style={{ background: cardBg, borderRadius: '16px', border: `1px solid ${cardBorder}`, maxWidth: '420px', width: '100%', overflow: 'hidden', boxShadow: mainShadow }}>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: isDark ? 'rgba(239, 68, 68, 0.15)' : '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <AlertTriangle size={18} color={isDark ? '#EF4444' : '#DC2626'} />
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: titleColor, margin: 0 }}>{t('subscriptions.excessAccounts.title')}</h3>
              </div>
              <p style={{ fontSize: '14px', color: textSecondary, marginBottom: '16px', lineHeight: '1.5' }}>{excessAccountsError.message}</p>
              <div style={{ background: isDark ? '#0F172B' : '#F8FAFC', borderRadius: '12px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                  <span style={{ color: textSecondary }}>{t('subscriptions.excessAccounts.activeAccounts')}</span>
                  <span style={{ fontWeight: 600, color: textPrimary }}>{excessAccountsError.activeAccountsCount}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                  <span style={{ color: textSecondary }}>{t('subscriptions.excessAccounts.allowedAccounts')}</span>
                  <span style={{ fontWeight: 600, color: textPrimary }}>{excessAccountsError.allowedAccounts}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                  <span style={{ color: textSecondary }}>{t('subscriptions.excessAccounts.accountsToRemove')}</span>
                  <span style={{ fontWeight: 600, color: '#EF4444' }}>{excessAccountsError.excessAccounts}</span>
                </div>
              </div>
            </div>
            <div style={{ padding: '0 20px 20px' }}>
              <button
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                onClick={() => setExcessAccountsError(null)}
              >
                {t('subscriptions.excessAccounts.close')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Subscriptions;
